import React from 'react';
import { Box, Container, Typography } from '@mui/material';

const TermsOfService = () => {
  return (
    <Box
      sx={{
        bgcolor: '#F5F5F7',
        minHeight: '100vh',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#1D1D1F',
        py: { xs: 6, md: 10 }
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '24px',
            p: { xs: 3, md: 6 },
            boxShadow: '0 20px 60px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.06)'
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 600, mb: 1 }}>
            Terms of Service
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 3 }}>
            Last Updated: December 23, 2025
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            These Terms of Service (“Terms”) govern your access to and use of the websites, applications, products, services, and related offerings provided by Beck Health, LLC (“Beck Health,” “we,” “us,” or “our”), including Aven (collectively, the “Services”).
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            By accessing or using the Services, you agree to be bound by these Terms. If you do not agree, you must not access or use the Services.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            1. Acceptance of Terms
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            By using the Services, you represent that:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>You are at least 18 years of age</li>
            <li>You have the legal capacity to enter into a binding agreement</li>
            <li>You will comply with these Terms and all applicable laws</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            If you are using the Services on behalf of an entity, you represent that you are authorized to bind that entity.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            2. Nature of the Services
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            No Medical Advice
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            Beck Health is not a healthcare provider. The Services:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Do not provide medical advice, diagnosis, or treatment</li>
            <li>Do not replace consultation with licensed healthcare professionals</li>
            <li>Are intended solely for supportive, informational, and adherence-focused purposes</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Always consult a qualified healthcare professional before making medical decisions.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            AI-Generated Content
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            The Services may include content generated by automated systems, including artificial intelligence. Such content:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Is informational only</li>
            <li>May be incomplete, inaccurate, or outdated</li>
            <li>Should not be relied upon as medical, legal, or professional advice</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Use of AI-generated content is at your own risk.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            3. User Accounts
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            You may be required to create an account to access certain features.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            You agree to:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Provide accurate and complete information</li>
            <li>Maintain the confidentiality of your credentials</li>
            <li>Notify us immediately of unauthorized access</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            You are responsible for all activity conducted through your account.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            4. User Conduct
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            You agree not to:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Use the Services for unlawful or harmful purposes</li>
            <li>Provide false, misleading, or deceptive information</li>
            <li>Attempt to reverse engineer or interfere with the Services</li>
            <li>Circumvent security features</li>
            <li>Upload malicious code or content</li>
            <li>Harass, abuse, or harm others</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            We reserve the right to suspend or terminate access for violations.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            5. Intellectual Property
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            All content, software, algorithms, designs, trademarks, and materials associated with the Services are owned by or licensed to Beck Health and are protected by intellectual property laws.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            You are granted a limited, non-exclusive, non-transferable, revocable license to use the Services solely for personal, lawful purposes.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            No rights are granted except as expressly stated.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            6. User Content
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            You retain ownership of content you submit, including messages and inputs (“User Content”).
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            By submitting User Content, you grant Beck Health a worldwide, royalty-free license to:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Use, process, store, analyze, and display User Content</li>
            <li>Provide and improve the Services</li>
            <li>Generate de-identified and aggregated insights</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            You represent that you have the right to submit such content.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            7. Payments and Subscriptions
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Certain Services may require payment.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            You agree that:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Fees are non-refundable except as required by law</li>
            <li>Subscription terms are disclosed at the time of purchase</li>
            <li>Beck Health may change pricing with notice</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Payment processing is handled by third-party providers.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            8. Third-Party Services
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            The Services may integrate or link to third-party services. Beck Health:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Does not control third-party services</li>
            <li>Is not responsible for third-party content or practices</li>
            <li>Disclaims liability for third-party services</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Use of third-party services is at your own risk.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            9. Termination
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            We may suspend or terminate your access at any time, with or without notice, for any reason, including violation of these Terms.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            Upon termination:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Your license to use the Services ends</li>
            <li>Certain provisions of these Terms survive, including limitation of liability and disclaimers</li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            10. Disclaimer of Warranties
          </Typography>

          <Typography variant="body1" sx={{ color: '#1D1D1F', mb: 2, lineHeight: 1.8, fontWeight: 600 }}>
            THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE.”
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8, fontWeight: 600 }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, BECK HEALTH DISCLAIMS ALL WARRANTIES, INCLUDING:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8, fontWeight: 600 }}>
            <li>IMPLIED WARRANTIES OF MERCHANTABILITY</li>
            <li>FITNESS FOR A PARTICULAR PURPOSE</li>
            <li>NON-INFRINGEMENT</li>
            <li>ACCURACY OR RELIABILITY OF CONTENT</li>
            <li>UNINTERRUPTED OR ERROR-FREE OPERATION</li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            11. Limitation of Liability
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8, fontWeight: 600 }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, BECK HEALTH SHALL NOT BE LIABLE FOR:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8, fontWeight: 600 }}>
            <li>INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
            <li>LOSS OF DATA, PROFITS, OR REVENUE</li>
            <li>PERSONAL INJURY OR HEALTH OUTCOMES</li>
            <li>RELIANCE ON AI-GENERATED CONTENT</li>
            <li>UNAUTHORIZED ACCESS OR SECURITY BREACHES BEYOND REASONABLE CONTROLS</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8, fontWeight: 600 }}>
            TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU TO BECK HEALTH IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            12. Indemnification
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            You agree to indemnify, defend, and hold harmless Beck Health and its officers, directors, employees, and affiliates from any claims, damages, liabilities, and expenses arising from:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Your use of the Services</li>
            <li>Your User Content</li>
            <li>Violation of these Terms</li>
            <li>Violation of applicable laws</li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            13. Governing Law and Venue
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            These Terms are governed by the laws of the State in which Beck Health, LLC is organized, without regard to conflict-of-law principles.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Any disputes shall be resolved exclusively in state or federal courts located within that jurisdiction.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            14. Arbitration and Class Action Waiver (Optional Section)
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            If you plan to include arbitration, this section can be activated.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            You agree that disputes may be resolved through binding arbitration and waive the right to participate in class actions.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            (Contact me if you want this fully enabled.)
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            15. Modifications to the Services or Terms
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            We may modify the Services or these Terms at any time. Changes will be effective upon posting. Continued use constitutes acceptance.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            16. Severability
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            If any provision of these Terms is found unenforceable, the remaining provisions remain in full force and effect.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            17. Entire Agreement
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            These Terms, together with the Privacy Policy, constitute the entire agreement between you and Beck Health regarding the Services.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            18. Contact Information
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Beck Health, LLC
            <br />
            Email: support@beckhealth.com
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsOfService;
